import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { getJsonRaw, saveJson } from '@/api/generateapp'
import { useProjectStore } from '@/store/projectStore'
import { refreshJourney, useJourneyStore } from '@/store/journeyStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { useJourneyAction } from '@/lib/journeyActions'
import type { RawAppData } from '@/types'
import { AppSettingsForm } from './AppSettingsForm'

/**
 * App settings (`/workbench/:project/settings`) — reachable from the app name
 * ▾ in the top bar, Advanced › App setup, and the three links on the Plan.
 * Saves through the same endpoint as the old Application Properties.
 */
export default function AppSettingsPage() {
  const { currentProject, fetchProjects } = useProjectStore()
  const perms = useProjectPermissions()
  const run = useJourneyAction()
  const journey = useJourneyStore((s) => s.journey)
  const folder = currentProject?.application.folder_name || ''
  const [appData, setAppData] = useState<RawAppData | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [epoch, setEpoch] = useState(0)

  const load = useCallback(async () => {
    if (!folder) return
    setLoadError(null)
    try {
      setAppData((await getJsonRaw(folder)) as RawAppData)
    } catch (err) {
      console.error('[settings] could not load application.json', err)
      setLoadError('The app settings could not be loaded.')
    }
  }, [folder])

  useEffect(() => { void load() }, [load])

  const save = async (application: RawAppData['application']) => {
    if (!appData) return
    setSaving(true)
    try {
      const updated = { ...appData, application }
      await saveJson(folder, updated)
      setAppData(updated)
      setEpoch((e) => e + 1)
      toast.success('App settings saved')
      await fetchProjects()
      // A changed App type changes the tabs straight away.
      refreshJourney()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail ? detail : 'Failed to save the app settings')
    } finally {
      setSaving(false)
    }
  }

  if (!currentProject) return null
  const nothingBuilt = (journey?.counts?.built ?? 1) === 0

  return (
    <div className="swb swb-page">
      <div className="swb-head">
        <div>
          <h1>App settings</h1>
          <p>The basics of your app. Technical settings are folded away at the bottom.</p>
        </div>
      </div>
      {loadError && (
        <div className="swb-notice swb-notice-warn">
          <span style={{ flex: 1 }}>{loadError}</span>
          <button type="button" className="swb-btn swb-btn-secondary" onClick={() => void load()}>Try again</button>
        </div>
      )}
      {!appData && !loadError && <div className="spinner" style={{ margin: '2rem auto' }} />}
      {appData && (
        <AppSettingsForm
          key={`${folder}:${epoch}`}
          appData={appData}
          onSave={save}
          saving={saving}
          canEdit={!perms.loaded || perms.canUpdateProperties}
          onBackToPlan={nothingBuilt ? () => void run('tab:plan') : undefined}
          onOpenPeople={() => void run(`tab:${journey?.tabs.find((t) => t.view === 'people')?.id || 'people'}`)}
          onOpenRightsRules={() => void run('tab:advanced/signin-rules')}
        />
      )}
    </div>
  )
}
